import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Package, Loader2, RotateCcw, Send, ChevronDown,
    CheckCircle, Navigation, Plus, Trash2, Table2,
    TrendingUp, TrendingDown,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchAssetItems,
    addAssetSaleItem,
    fetchAssetSaleItemDetails,
    fetchAssetAmount,
    deleteAssetSaleItem,
    saveAssetSaleData,
    clearAddItemStatus,
    clearDeleteStatus,
    clearSaveResult,
    resetAssetSale,
    selectAssetItemsArray,
    selectAssetItemsLoading,
    selectStagedItemsArray,
    selectStagedItemsLoading,
    selectAssetValue,
    selectAddItemStatus,
    selectAddItemLoading,
    selectAddItemError,
    selectDeleteStatus,
    selectDeleteLoading,
    selectDeleteError,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/purchaseSlice/assetSaleSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const MIN_DATE = new Date('2022-04-01');
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const AssetSale = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────
    const assetItems = useSelector(selectAssetItemsArray);
    const assetItemsLoading = useSelector(selectAssetItemsLoading);
    const stagedItems = useSelector(selectStagedItemsArray);
    const stagedItemsLoading = useSelector(selectStagedItemsLoading);
    const assetValue = useSelector(selectAssetValue);
    const addItemStatus = useSelector(selectAddItemStatus);
    const addItemLoading = useSelector(selectAddItemLoading);
    const addItemError = useSelector(selectAddItemError);
    const deleteStatus = useSelector(selectDeleteStatus);
    const deleteLoading = useSelector(selectDeleteLoading);
    const deleteError = useSelector(selectDeleteError);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    // ── Form state ────────────────────────────────────────────────────────
    const [selectedItemId, setSelectedItemId] = useState('');
    const [checkedRows, setCheckedRows] = useState({}); // { [IId]: boolean }
    const [bookValueDate, setBookValueDate] = useState(null);
    const [assetAmount, setAssetAmount] = useState('');
    const [sellAmount, setSellAmount] = useState('');
    const [partyName, setPartyName] = useState('');
    const [partyAddress, setPartyAddress] = useState('');
    const [saleDate, setSaleDate] = useState(new Date());
    const [remarks, setRemarks] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    const loadGrid = () => {
        dispatch(fetchAssetSaleItemDetails());
        dispatch(fetchAssetAmount());
    };

    useEffect(() => {
        if (userId) {
            dispatch(fetchAssetItems({ uid: userId, rid: roleId }));
        }
        loadGrid();
        return () => {
            dispatch(resetAssetSale());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Add-item result effect
    useEffect(() => {
        if (addItemStatus === 'success') {
            dispatch(clearAddItemStatus());
            setSelectedItemId('');
            loadGrid();
        } else if (addItemStatus === 'failed' && addItemError) {
            toast.error(addItemError);
            dispatch(clearAddItemStatus());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addItemStatus]);

    // Delete result effect
    useEffect(() => {
        if (deleteStatus === 'success') {
            toast.success('Deleted Successfully.');
            dispatch(clearDeleteStatus());
            loadGrid();
        } else if (deleteStatus === 'failed' && deleteError) {
            toast.error(deleteError);
            dispatch(clearDeleteStatus());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteStatus]);

    // Save result effect
    useEffect(() => {
        if (saveStatus === 'success') {
            const [, requestNo] = (saveError || '').split(',');
            toast.success(`Request No ${requestNo || ''} Generated Successfully`);
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleFullReset = () => {
        setSelectedItemId(''); setCheckedRows({});
        setBookValueDate(null); setAssetAmount(''); setSellAmount('');
        setPartyName(''); setPartyAddress(''); setSaleDate(new Date()); setRemarks('');
        dispatch(resetAssetSale());
        if (userId) {
            dispatch(fetchAssetItems({ uid: userId, rid: roleId }));
        }
        loadGrid();
    };

    const handleAddItem = () => {
        if (!selectedItemId) { toast.warn('Select Asset ItemCode.'); return; }
        dispatch(addAssetSaleItem({ itemId: selectedItemId, createdBy: userName }));
    };

    const toggleCheck = (iId) => {
        setCheckedRows((prev) => ({ ...prev, [iId]: !prev[iId] }));
    };

    const handleDeleteRow = (iId) => {
        if (!checkedRows[iId]) { toast.warn('Please check the row to delete.'); return; }
        dispatch(deleteAssetSaleItem(iId));
    };

    const handleAssetAmountChange = (e) => {
        const val = e.target.value;
        if (val !== '' && !/^\d*\.?\d{0,2}$/.test(val)) return;
        const numVal = parseFloat(val);
        if (val !== '' && !isNaN(numVal) && numVal > assetValue) {
            toast.error('Invalid Actual Asset Value');
            setAssetAmount('');
            return;
        }
        setAssetAmount(val);
    };

    const handleSellAmountChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) setSellAmount(val);
    };

    const numAssetAmt = parseFloat(assetAmount) || 0;
    const numSellAmt = parseFloat(sellAmount) || 0;
    const profit = numSellAmt > numAssetAmt ? (numSellAmt - numAssetAmt) : 0;
    const loss = numAssetAmt > numSellAmt ? (numAssetAmt - numSellAmt) : 0;

    const allChecked = stagedItems.length > 0 && stagedItems.every((r) => checkedRows[r.IId]);

    const handleSubmit = () => {
        if (stagedItems.length === 0) { toast.warn('Please Add Asset Sale Item.'); return; }
        if (!allChecked) { toast.warn('Please Verify Item Code.'); return; }
        const codes = stagedItems.map((r) => r.ItemCode);
        if (new Set(codes).size !== codes.length) { toast.warn('Duplicate ItemCode for Asset Sale.'); return; }
        if (!bookValueDate) { toast.warn('Select Asset Book Value Date As on.'); return; }
        if (!assetAmount) { toast.warn('Add Asset As on Value.'); return; }
        if (!sellAmount) { toast.warn('Add Asset Selling Value.'); return; }
        if (!partyName.trim()) { toast.warn('Enter Buyer Name.'); return; }
        if (!partyAddress.trim()) { toast.warn('Enter Buyer Address.'); return; }
        if (!saleDate) { toast.warn('Enter Sale Date.'); return; }
        if (!remarks.trim()) { toast.warn('Select Remarks.'); return; }

        const lastRow = stagedItems[stagedItems.length - 1];

        dispatch(saveAssetSaleData({
            itemId: lastRow.IId,
            itemCode: lastRow.ItemCode?.trim(),
            date: formatDateForAPI(saleDate),
            bookValueDate: formatDateForAPI(bookValueDate),
            partyName,
            partyAddress,
            assetAmount,
            sellAmount,
            remarks,
            profit: String(profit.toFixed(2)),
            loss: String(loss.toFixed(2)),
            roleId,
            createdBy: userName,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || addItemLoading || deleteLoading;

    const showSummary = stagedItems.length > 0 && bookValueDate && assetAmount && sellAmount &&
        partyName.trim() && partyAddress.trim() && saleDate && remarks.trim();
    const canSubmit = showSummary && !saveLoading;

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <Package className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Purchase Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Asset Sale'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Sell a fixed asset and record the profit/loss</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Purchase / Asset Sale</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Select Item ────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={Package}
                        title="Select Asset Item"
                        subtitle="Add the asset item to sell"
                        action={
                            <button
                                onClick={handleFullReset}
                                disabled={isBusy}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                            <div className="md:col-span-2">
                                <Label required>Item Code</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={selectedItemId}
                                        onChange={(e) => setSelectedItemId(e.target.value)}
                                        disabled={assetItemsLoading || isBusy}
                                    >
                                        <option value="">{assetItemsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {assetItems.map((it) => (
                                            <option key={it.Itemid} value={it.Itemid}>{it.ItemName}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={assetItemsLoading} />
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={handleAddItem}
                                    disabled={!selectedItemId || isBusy}
                                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {addItemLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Adding…</>
                                        : <><Plus className="h-4 w-4" /> Add</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Staged Items Grid ──────────────────────── */}
                {stagedItems.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader
                            icon={Table2}
                            title="Asset Sale Item"
                            subtitle="Check the row to verify it — required before delete and submit"
                        />
                        <div className="p-6 md:p-8">
                            {stagedItemsLoading ? (
                                <div className="flex items-center justify-center py-8 text-gray-400">
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                                                <th className="px-4 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Chk</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Item Code</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Item Name</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Specification</th>
                                                <th className="px-4 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Basic Price</th>
                                                <th className="px-4 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Units</th>
                                                <th className="px-4 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Amount</th>
                                                <th className="px-4 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stagedItems.map((r, idx) => (
                                                <tr key={r.IId} className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                                                    <td className="px-4 py-3 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!checkedRows[r.IId]}
                                                            onChange={() => toggleCheck(r.IId)}
                                                            className="accent-indigo-600 h-4 w-4"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100">{r.ItemCode}</td>
                                                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-medium">{r.ItemName}</td>
                                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.Specification}</td>
                                                    <td className="px-4 py-3 text-right text-gray-800 dark:text-gray-100">{r.BasicPrice}</td>
                                                    <td className="px-4 py-3 text-right text-gray-800 dark:text-gray-100">{r.Units}</td>
                                                    <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(r.Amount)}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <button
                                                            onClick={() => handleDeleteRow(r.IId)}
                                                            disabled={isBusy}
                                                            className="p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-40"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ── Book Value / Amount / Sell Amount ─────────── */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                                <div>
                                    <Label required>Book Value Date As On</Label>
                                    <CustomDatePicker
                                        value={bookValueDate}
                                        onChange={setBookValueDate}
                                        disabled={isBusy}
                                        minDate={MIN_DATE}
                                        maxDate={new Date()}
                                    />
                                </div>
                                <div>
                                    <Label required>Asset As-On Value (₹)</Label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        className={inputCls}
                                        value={assetAmount}
                                        onChange={handleAssetAmountChange}
                                        disabled={isBusy}
                                    />
                                    {assetValue > 0 && (
                                        <p className="text-[10px] text-gray-400 mt-1">Max allowed: ₹ {formatIndianCurrency(assetValue)}</p>
                                    )}
                                </div>
                                <div>
                                    <Label required>Selling Value (₹)</Label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        className={inputCls}
                                        value={sellAmount}
                                        onChange={handleSellAmountChange}
                                        disabled={isBusy}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div className="rounded-xl p-4 border-2 border-emerald-500 flex items-center justify-between">
                                    <span className="flex items-center gap-2 text-sm font-bold text-emerald-600"><TrendingUp className="h-4 w-4" /> Profit</span>
                                    <span className="text-lg font-black text-emerald-600">₹ {formatIndianCurrency(profit)}</span>
                                </div>
                                <div className="rounded-xl p-4 border-2 border-rose-500 flex items-center justify-between">
                                    <span className="flex items-center gap-2 text-sm font-bold text-rose-600"><TrendingDown className="h-4 w-4" /> Loss</span>
                                    <span className="text-lg font-black text-rose-600">₹ {formatIndianCurrency(loss)}</span>
                                </div>
                            </div>

                            {/* ── Party Details ──────────────────────────────── */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                                <div>
                                    <Label required>Buyer Name</Label>
                                    <input
                                        type="text"
                                        className={inputCls}
                                        placeholder="Enter buyer name"
                                        value={partyName}
                                        onChange={(e) => setPartyName(e.target.value)}
                                        disabled={isBusy}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label required>Buyer Address</Label>
                                    <textarea
                                        rows={1}
                                        placeholder="Enter buyer address…"
                                        value={partyAddress}
                                        onChange={(e) => setPartyAddress(e.target.value)}
                                        disabled={isBusy}
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                                <div>
                                    <Label required>Sale Date</Label>
                                    <CustomDatePicker
                                        value={saleDate}
                                        onChange={setSaleDate}
                                        disabled={isBusy}
                                        minDate={MIN_DATE}
                                        maxDate={new Date()}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label required>Remarks</Label>
                                    <textarea
                                        rows={1}
                                        placeholder="Enter remarks…"
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        disabled={isBusy}
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Summary ───────────────────────────────────────────── */}
                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="Asset Sale Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {[
                                    { label: 'Item(s)', value: stagedItems.map((r) => r.ItemCode).join(', ') },
                                    { label: 'Book Value Date', value: formatDateForAPI(bookValueDate) },
                                    { label: 'Asset Value', value: `₹ ${formatIndianCurrency(numAssetAmt)}` },
                                    { label: 'Selling Value', value: `₹ ${formatIndianCurrency(numSellAmt)}` },
                                    { label: 'Profit', value: `₹ ${formatIndianCurrency(profit)}` },
                                    { label: 'Loss', value: `₹ ${formatIndianCurrency(loss)}` },
                                    { label: 'Buyer', value: partyName },
                                    { label: 'Sale Date', value: formatDateForAPI(saleDate) },
                                    { label: 'Created By', value: userName },
                                    { label: 'Buyer Address', value: partyAddress, wide: true },
                                    { label: 'Remarks', value: remarks, wide: true },
                                ].map(({ label, value, wide }) => (
                                    <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                        <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ────────────────────────────────────── */}
                <div className="flex items-center justify-end gap-3 pb-6">
                    <button
                        onClick={handleFullReset}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {saveLoading
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                            : <><Send className="h-4 w-4" /> Submit</>}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AssetSale;
